import i18n from "../i18n";
import validationUtils from "../validationUtils";
import { customerConfig } from '../../config';
import addressTypes from "../../config/addressTypes"

const getCommonSchema = (evidenceType, msg) => {
  return {
    type: 'object',
    title: evidenceType.label,
    properties: {
      homeAddress: {
        type: "object",
        properties: {
          address: {
            title: msg('field.address.title'),
            type: "string",
            minLength: 10
          },
          uprn: {
            title: msg('field.addressRef.title'),
            type: "string"
          }
        },
        required: ['address']
      },
      images: {
        type: "array",
        minItems: 1,
        title: msg('field.images.title'),
        items: {
          type: "string",
          format: "data-url"
        }
      }
    },
    required: ['homeAddress', 'images']
  }
};

const getCommonUiSchema = msg => {

  const uiSchema = {
    "ui:order": [
      "homeAddress",
      "*",
      "images"
    ],
    homeAddress: {
      address: {},
      uprn: {
        "ui:widget": "hidden"
      }
    }
  };

  const addressType = customerConfig.addressType;

  switch (addressType) {
    // use a custom field component for homeAddress object
    case addressTypes.lookingLocal:
      uiSchema.homeAddress["ui:field"] = addressType.fieldName;
      uiSchema.homeAddress["ui:help"] = msg('field.address.help');
      break;

    default:
      throw new Error(`Unrecognised address type: ${addressType}`);
  }

  return uiSchema;
};

const getCommonValidationMessages = () => {
  return {
    '.homeAddress.address': 'field.address.error',
    '.images': 'field.images.error'
  }
};

const customFormValidator = customFieldValidators => {

  return (formData, errors) => {
    customFieldValidators.forEach(validator => {
      validator(formData, errors);
    });

    return errors;
  };
};

const IGNORE_PROPERTY_NAMES = [
  'category',
  'category-cy',
  'helpMessage',
  'helpMessage-cy',
  'label',
  'label-cy',
  'verificationRequired',
  'key',
  'required',
  'description',
  'checkDatabase',
  'suggestions', // used for autocomplete
  'options' // used with select
];

// Formats supported by RJSF
// https://github.com/mozilla-services/react-jsonschema-form#string-formats
const RJSF_STRING_FORMATS = ['date', 'email', 'uri', 'data-url', 'date-time'];

const NOOP_VALIDATOR = (formData, errors) => errors;

const buildFieldDefinition = (field => {

  const fieldSchema = {
    type: 'string',
    title: i18n.getTranslation(field, 'label')
  };

  const fieldUiSchema = {
    'ui:help': i18n.getTranslation(field, 'helpMessage')
  };

  let fieldValidator = NOOP_VALIDATOR;

  for (let key in field) {
    if (field.hasOwnProperty(key) && IGNORE_PROPERTY_NAMES.indexOf(key) === -1) {

      if (key === 'pattern') {
        // We don't need a pattern in the schema to validate string formats supported by RJSF.
        // We also don't need it if it's a checkDigit field, because the checkDigit validator (validationUtils.js) also
        // validates the pattern
        if (RJSF_STRING_FORMATS.indexOf(field.format) === -1 && !field.checkDigit) {
          fieldSchema.pattern = field[key]
        }
      } else if (key === 'format') {
        const formatType = field[key];

        // Formats supported by taxonomy.json
        // https://github.com/lookinglocal/scanstation-config/blob/master/src/schema/taxonomy.json
        switch (formatType) {
          case 'email':
            fieldSchema.format = formatType;
            break;

          case 'date':
            fieldValidator = validationUtils.createValidator(field, validationUtils.isPastDate);
            fieldSchema.format = formatType;
            break;

          case 'select':
            if (!field.options || !field.options.length) {
              throw new Error(`Format ${formatType} requires options array to be present`);
            }
            fieldSchema["enum"] = field.options;
            break;

          case 'phone':
            // RJSF doesn't support a 'phone' value for the format property of a schema
            // https://github.com/mozilla-services/react-jsonschema-form#string-formats
            fieldUiSchema["ui:options"] = {
              inputType: 'tel'
            };
            break;

          case 'number':
            // fields which have format: "number" should NOT have their schema type set to number because they typically
            // also specify a pattern which can only be applied to strings. Instead, just use an
            // <input type="number"> so only digits can be entered.
            fieldUiSchema["ui:options"] = {
              inputType: 'number'
            };
            break;

          default:
            throw new Error(`Unrecognised format value: ${formatType}`);
        }
      } else if (key === 'checkDigit') {
        const checkDigitValidator = validationUtils.createCheckDigitValidator(field[key], field.pattern);
        fieldValidator = validationUtils.createValidator(field, checkDigitValidator);

      } else {
        throw new Error(`Unrecognised schema property: ${key}`);
      }
    }
  }

  return {fieldSchema, fieldUiSchema, fieldValidator};
});

/**
 * Given a form defined in the JSON taxonomy, convert it to the following RJSF inputs
 *
 *  - schema
 *  - uiSchema
 *  - custom validation messages
 *  - custom validators
 *
 * @param taxonomyForm
 * @param evidenceType
 * @param msg a function that translates messages for the current locale
 */
export default (taxonomyForm, evidenceType, msg) => {

  const schema = getCommonSchema(evidenceType, msg);
  const uiSchema = getCommonUiSchema(msg);
  const validationMessages = getCommonValidationMessages();

  const formDefinition = {
    schema,
    uiSchema,
    validationMessages,
    formValidator: NOOP_VALIDATOR
  };

  if (!taxonomyForm) {
    return formDefinition;
  }

  const customValidators = [];

  taxonomyForm.fields.forEach(field => {
    const schemas = buildFieldDefinition(field);

    if (!field.key) {
      if (formDefinition.introText) {
        throw new Error("Only a single field without a key is permitted");
      }
      formDefinition.introText = i18n.getTranslation(field)

    } else {
      schema.properties[field.key] = schemas.fieldSchema;

      if (field.required) {
        schema.required.push(field.key);
      }
      uiSchema[field.key] = schemas.fieldUiSchema;

      // Use the same message as the help and validation error message, if one is defined
      const helpMessage = i18n.getTranslation(field, 'helpMessage');

      if (helpMessage) {
        validationMessages[`.${field.key}`] = helpMessage
      }

      customValidators.push(schemas.fieldValidator)
    }
  });

  if (taxonomyForm.orFields) {
    taxonomyForm.orFields.forEach(orFieldGroup => {
      const fieldKeys = orFieldGroup.keys;
      const errorMessage = i18n.getTranslation(orFieldGroup, 'message');

      const orFieldsValidator = (formData, errors) => {
        const fieldWithValue = fieldKeys.find(fieldKey => formData[fieldKey]);

        if (!fieldWithValue) {
          // register an error on all the fields in the group
          fieldKeys.forEach(fieldKey => {
            errors[fieldKey].addError(errorMessage);
          });
        }
      };

      customValidators.push(orFieldsValidator);
    })
  }

  formDefinition.formValidator = customFormValidator(customValidators);
  return formDefinition;
};
